import { StatusKey } from '@/Enums/Agencies/StatusKey.enum';
import { IAgencyGridRow } from '@/Interfaces/Agency.interface';
import { IControlOption } from '@/Interfaces/ControlOption.interface';
import { IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import { formatAgenciesResponseData, transformAgenciesFilters } from '@/helpers/agencies';
import { pendingCaseHandler, rejectedCaseHandler } from '@/helpers/forecaster/slice';
import { RootState } from '@/store';
import { fetchAgencies } from '@/store/api/agencies.api';
import { SearchAgenciesFilters } from '@/types/agencies.types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

enum actionPrefix {
  FETCH_AGENCIES = 'agenciesSlice/fetch',
}

interface IState {
  data: IAgencyGridRow[];
  dataRequestId: string | null;
  selectedAgency: IAgencyGridRow | null;
  total: number;
  filters: SearchAgenciesFilters;
  advertisers: IControlOption[];
  advertisersRequestId: string | null;
}

const initialState: IState = {
  data: [],
  dataRequestId: null,
  total: 0,
  selectedAgency: null,
  filters: {
    page: 1,
    limit: 15,
    search: '',
    sort: [{ field: 'name', sort: 'asc' }],
    status: StatusKey.ALL,
    whitelabels: [],
    clients: [],
  },
  advertisers: [],
  advertisersRequestId: null,
};

const agenciesSlice = createSlice({
  name: 'agencies',
  initialState,
  reducers: {
    setSelectedAgency: (state, { payload }: { payload: IAgencyGridRow }) => {
      state.selectedAgency = payload;
    },
    clearSelectedAgency: state => {
      state.selectedAgency = null;
    },
    setFilters: (state, { payload }: { payload: Partial<SearchAgenciesFilters> }) => {
      state.filters = { ...state.filters, ...payload };
    },
    resetAgenciesPageState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getAgencies.fulfilled, (state, { meta, payload }) => {
        if (state.dataRequestId !== meta.requestId) return;
        state.dataRequestId = null;
        state.data = payload.data;
        state.total = payload.total;
        if (!payload.data.length && state.filters.page > 1) {
          state.filters.page = 1;
        }
      })
      .addCase(getAgencies.pending, pendingCaseHandler('data'))
      .addCase(getAgencies.rejected, rejectedCaseHandler('data'));
  },
});

export default agenciesSlice.reducer;

export const { setSelectedAgency, clearSelectedAgency, setFilters, resetAgenciesPageState } = agenciesSlice.actions;

export const getAgencies = createAsyncThunk<
  { data: IAgencyGridRow[]; total: number },
  undefined,
  IRejectValueThunkConfig
>(actionPrefix.FETCH_AGENCIES, async (_, { rejectWithValue, getState }) => {
  const {
    agencies: { filters },
  } = getState() as RootState;

  const transformedFilters = transformAgenciesFilters(filters);

  try {
    const response = await fetchAgencies(transformedFilters);
    return formatAgenciesResponseData(response);
  } catch (err) {
    if (err instanceof Error) {
      return rejectWithValue(err);
    }
    return rejectWithValue(new Error('An unknown error occurred'));
  }
});
